import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import TeachersList from "./components/teachers";
import FormatorsList from "./components/formators";
import NonAcademicStaffList from "./components/non-academic staffs";
import VerifyResult from "./components/verify-result";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const adminCards = [
  {
    key: "students",
    icon: <Icon name="Users" size={36} className="text-primary mb-2" />,
    title: "Students",
    desc: "View all students by class",
  },
  {
    key: "teachers",
    icon: <Icon name="UserCheck" size={36} className="text-accent mb-2" />,
    title: "Teachers",
    desc: "See all teachers in the school",
  },
  {
    key: "verify",
    icon: <Icon name="CheckCircle" size={36} className="text-green-500 mb-2" />,
    title: "Verify Results",
    desc: "Verify student results",
  },
];

const AdminDashboard = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [formators, setFormators] = useState([]);
  const [nonAcademic, setNonAcademic] = useState([]);
  const [showSection, setShowSection] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [editingStudentId, setEditingStudentId] = useState(null);
  const [editingStudentName, setEditingStudentName] = useState("");
  const [savingStudentId, setSavingStudentId] = useState(null);
  const [deletingStudentId, setDeletingStudentId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null); // For confirmation dialog
  const [successMsg, setSuccessMsg] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    fetchClasses();
    fetchTeachers();
    fetchFormators();
    fetchNonAcademic();
  }, []);

 useEffect(() => {
  if (location.state?.showSection === "verify") {
    setShowSection("verify");

    navigate("/admin", {
      replace: true,
      state: {},
    });
  }
}, [location.state, navigate]);

  const fetchClasses = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const adminData = JSON.parse(localStorage.getItem("adminData") || "{}");
      const term = adminData.term;

      const res = await axios.get(`${API_BASE_URL}/api/user/class-list?term=${term}`);
      setClasses(res.data || []);
    } catch (err) {
      setErrorMsg("Failed to fetch classes.");
    }
    setLoading(false);
  };

  const fetchTeachers = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await axios.get(`${API_BASE_URL}/api/user?role=teacher`);
      setTeachers(res.data || []);
    } catch (err) {
      setErrorMsg("Failed to fetch teachers.");
    }
    setLoading(false);
  };

  const fetchFormators = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await axios.get(`${API_BASE_URL}/api/formators`);
      setFormators(res.data || []);
    } catch (err) {
      setErrorMsg("Failed to fetch formators.");
    }
    setLoading(false);
  };

  const fetchNonAcademic = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await axios.get(`${API_BASE_URL}/api/non-academic`);
      setNonAcademic(res.data || []);
    } catch (err) {
      setErrorMsg("Failed to fetch non-academic staff.");
    }
    setLoading(false);
  };

  const handleClassSelect = async (className) => {
    setSelectedClass(className);
    setLoading(true);
    setErrorMsg("");
    try {
      const studentsRes = await axios.get(
        `${API_BASE_URL}/api/user/class/${className}/all/students`
      );
      setStudents(studentsRes.data || []);
    } catch (err) {
      setErrorMsg("Failed to fetch class students.");
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminData");
    window.location.replace("/login-authentication");
  };

  const filterUsers = (users) => {
    if (!search.trim()) return users;
    const searchTerm = search.toLowerCase();

    return users.filter((user) => {
      // Safely convert to strings, handling null/undefined values
      const name = String(user.name || '').toLowerCase();
      const className = String(user.className || '').toLowerCase();

      return name.includes(searchTerm) || className.includes(searchTerm);
    });
  };

  const handleSection = async (section) => {
    setShowSection(section);
    setSelectedClass(null);
    setSearch("");
    setErrorMsg("");
    if (section === "teachers") fetchTeachers();
    if (section === "formators") fetchFormators();
    if (section === "nonAcademic") fetchNonAcademic();
  };

  const handleStudentNameEdit = (student) => {
    setEditingStudentId(student._id);
    setEditingStudentName(student.name);
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 100);
  };

  const handleStudentNameChange = (e) => {
    setEditingStudentName(e.target.value);
  };

  const handleStudentNameSave = async (student) => {
    if (!editingStudentName.trim() || editingStudentName === student.name) {
      setEditingStudentId(null);
      setEditingStudentName("");
      return;
    }
    setSavingStudentId(student._id);
    try {
      await axios.put(`${API_BASE_URL}/api/user/${student._id}/update-name`, {
        name: editingStudentName.trim(),
      });
      setStudents((prev) =>
        prev.map((s) =>
          s._id === student._id ? { ...s, name: editingStudentName.trim() } : s
        )
      );
      setEditingStudentId(null);
      setEditingStudentName("");
      setSuccessMsg("Student name updated successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg("Failed to update student name.");
    } finally {
      setSavingStudentId(null);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    setDeletingStudentId(studentId);
    setErrorMsg("");
    try {
      await axios.delete(`${API_BASE_URL}/api/user/${studentId}`);

      // Remove student from local state immediately
      setStudents((prev) => prev.filter((s) => s._id !== studentId));

      // Update class count
      setClasses((prev) =>
        prev.map((cls) =>
          cls.className === selectedClass
            ? { ...cls, studentCount: Math.max(0, cls.studentCount - 1) }
            : cls
        )
      );

      setDeleteConfirmId(null);
      setSuccessMsg("Student deleted successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg(err.response?.data?.error || "Failed to delete student.");
    } finally {
      setDeletingStudentId(null);
    }
  };

  const DeleteConfirmationDialog = ({ student, onConfirm, onCancel, isDeleting }) => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-sm shadow-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Delete Student?
        </h3>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{student.name}</strong> from {selectedClass}?
        </p>
        <p className="text-red-600 text-sm mb-6 font-semibold">
          ⚠️ This action cannot be undone.
        </p>
        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background relative">
      {/* Admin Dashboard Header */}
      <div className="w-full flex flex-col sm:flex-row justify-between items-center px-4 py-4 bg-white shadow-sm border-b border-border">
        <h1 className="text-2xl font-heading font-bold text-primary mb-2 sm:mb-0">
          Admin Dashboard
        </h1>
        <Button variant="destructive" size="sm" onClick={handleLogout}>
          Logout
        </Button>
      </div>

      {/* Horizontal Cards Section (Only on main dashboard) */}
      {!showSection && (
        <div className="w-full bg-white shadow-sm border-b border-border py-4 px-2 flex flex-wrap justify-center gap-4 max-w-5xl mx-auto">
          {adminCards.map((card) => (
            <div
              key={card.key}
              className="min-w-[180px] max-w-xs flex-shrink-0 bg-card border border-border rounded-xl p-4 flex flex-col items-center cursor-pointer hover:shadow-lg transition"
              onClick={() =>
                card.key === "teachers"
                  ? setShowSection("teachers")
                  : handleSection(card.key)
              }
            >
              {card.icon}
              <h2 className="font-heading font-bold text-base mb-1">{card.title}</h2>
              <p className="text-muted-foreground text-xs text-center">{card.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-2 sm:p-4 transition-all duration-200">
        {/* Error Message */}
        {errorMsg && (
          <div className="mb-4 text-center text-red-600 bg-red-50 border border-red-200 rounded p-3 flex items-center justify-between">
            <span>{errorMsg}</span>
            <button onClick={() => setErrorMsg("")} className="text-red-600 hover:text-red-800">
              ✕
            </button>
          </div>
        )}

        {/* Success Message */}
        {successMsg && (
          <div className="mb-4 text-center text-green-600 bg-green-50 border border-green-200 rounded p-3 flex items-center justify-between">
            <span>{successMsg}</span>
            <button onClick={() => setSuccessMsg("")} className="text-green-600 hover:text-green-800">
              ✕
            </button>
          </div>
        )}

        {/* Admin Quick Stats */}
        {!showSection && (
          <div className="mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex flex-col items-center">
                <Icon name="Users" size={28} className="text-primary mb-2" />
                <div className="text-lg font-bold text-primary">
                  {classes.reduce((acc, cls) => acc + (cls.studentCount || 0), 0)}
                </div>
                <div className="text-xs text-muted-foreground">Total Students</div>
              </div>
           <div className="bg-accent/20 border border-accent/40 rounded-lg p-4 flex flex-col items-center">
  <Icon name="UserCheck" size={28} className="text-accent mb-2" />
  <div className="text-lg font-bold text-accent">
    {teachers.length}
  </div>
  <div className="text-xs text-muted-foreground">Total Teachers</div>
</div>
            </div>
            {/* Professional Admin Notices */}
            <div className="bg-gradient-to-br from-blue-50 via-white to-blue-100 border border-blue-200 rounded-lg p-6 mb-8 shadow">
              <h2 className="font-heading font-bold text-lg mb-3 text-primary flex items-center gap-2">
                <Icon name="Info" size={22} className="text-primary" />
                Admin Notices
              </h2>
              <ul className="list-none space-y-3 text-base text-blue-900 font-medium">
                <li className="flex items-center gap-2">
                  <Icon name="Database" size={18} className="text-blue-500" />
                  Regularly back up the database to prevent data loss.
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="CheckCircle" size={18} className="text-green-500" />
                  Review and approve pending result verifications promptly.
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Eye" size={18} className="text-indigo-500" />
                  Monitor user activity for unusual behavior.
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Edit" size={18} className="text-yellow-500" />
                  Update staff and student records as needed.
                </li>
                <li className="flex items-center gap-2">
                  <Icon name="Bell" size={18} className="text-red-500" />
                  Check for new announcements to post.
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Students Section: Show classes, then students in class */}
        {showSection === "students" && !selectedClass && (
          <>
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => setShowSection(null)}
            >
              &larr; Back to Dashboard
            </Button>
            <div className="mb-4 text-lg font-semibold text-primary">
              Total Students in School:{" "}
              {classes.reduce((acc, cls) => acc + (cls.studentCount || 0), 0)}
            </div>
            <h2 className="font-heading text-xl mb-4">All Classes</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {classes.map((cls) => (
                <div
                  key={cls.className}
                  className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:shadow-lg"
                  onClick={() => handleClassSelect(cls.className)}
                >
                  <div className="font-bold text-lg">{cls.className}</div>
                  <div className="text-muted-foreground text-sm">
                    Teacher: {cls.teacherName || "N/A"}
                  </div>
                  <div className="text-muted-foreground text-sm">
                    Students: {cls.studentCount || 0}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Students in selected class */}
        {showSection === "students" && selectedClass && (
          <>
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => setSelectedClass(null)}
            >
              &larr; Back to Classes
            </Button>
            <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="font-heading text-xl">
                Students in {selectedClass} ({students.length})
              </h2>
              <input
                type="text"
                placeholder="Search students..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full sm:w-64 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <div className="space-y-2">
              {filterUsers(students).length > 0 ? (
                filterUsers(students).map((student) => (
                  <div
                    key={student._id}
                    className="flex items-center justify-between bg-muted/10 rounded-lg px-3 py-3 hover:bg-primary/10 transition"
                  >
                    <span className="font-semibold flex items-center gap-2 flex-1">
                      {editingStudentId === student._id ? (
                        <>
                          <input
                            ref={inputRef}
                            type="text"
                            value={editingStudentName}
                            onChange={handleStudentNameChange}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleStudentNameSave(student);
                              if (e.key === "Escape") {
                                setEditingStudentId(null);
                                setEditingStudentName("");
                              }
                            }}
                            className="border border-primary rounded px-2 py-1 text-sm flex-1"
                            disabled={savingStudentId === student._id}
                          />
                          <button
                            className="ml-2 text-green-600 font-bold hover:text-green-700 text-sm"
                            title="Save"
                            onClick={() => handleStudentNameSave(student)}
                            disabled={savingStudentId === student._id}
                          >
                            {savingStudentId === student._id ? "Saving..." : "Save"}
                          </button>
                          <button
                            className="ml-1 text-red-500 font-bold hover:text-red-700 text-sm"
                            title="Cancel"
                            onClick={() => {
                              setEditingStudentId(null);
                              setEditingStudentName("");
                            }}
                            disabled={savingStudentId === student._id}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          {student.name}
                          <button
                            className="ml-2 text-xs text-blue-600 underline hover:text-blue-800"
                            title="Edit Name"
                            onClick={() => handleStudentNameEdit(student)}
                          >
                            Edit
                          </button>
                        </>
                      )}
                    </span>
                    <span className="flex items-center gap-2 flex-shrink-0 ml-4">
                      <span className="text-xs text-muted-foreground">
                        {student.className}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/admin/student/${student._id}`)}
                        title="View Profile"
                      >
                        <Icon name="User" size={16} className="mr-1" />
                        View
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeleteConfirmId(student._id)}
                        disabled={deletingStudentId === student._id}
                        title="Delete Student"
                      >
                        <Icon name="Trash2" size={16} className="mr-1" />
                        {deletingStudentId === student._id ? "..." : "Delete"}
                      </Button>
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No students found.
                </div>
              )}
            </div>
          </>
        )}

        {/* Teachers Section */}
        {showSection === "teachers" && (
          <>
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => setShowSection(null)}
            >
              &larr; Back to Dashboard
            </Button>
            <TeachersList />
          </>
        )}

        {/* Formators Section */}
        {showSection === "formators" && (
          <>
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => setShowSection(null)}
            >
              &larr; Back to Dashboard
            </Button>
            <FormatorsList />
          </>
        )}

        {/* Non-Academic Staff Section */}
        {showSection === "nonAcademic" && (
          <>
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => setShowSection(null)}
            >
              &larr; Back to Dashboard
            </Button>
            <NonAcademicStaffList />
          </>
        )}

        {/* Verify Results Section */}
        {showSection === "verify" && (
          <>
            <Button
              variant="outline"
              className="mb-4"
              onClick={() => setShowSection(null)}
            >
              &larr; Back to Dashboard
            </Button>
            <VerifyResult
              initialClass={location.state?.selectedClass}
              initialTerm={location.state?.selectedTerm}
            />
          </>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (
        <DeleteConfirmationDialog
          student={students.find((s) => s._id === deleteConfirmId)}
          onConfirm={() => handleDeleteStudent(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
          isDeleting={deletingStudentId === deleteConfirmId}
        />
      )}
    </div>
  );
};

export default AdminDashboard;